import * as React from "react";
import { OscrLevelTwoRecord, KeyStringValue } from "../../interfaces";
import AddressDisplay from "../AddressDisplay";
import {
  FROM_OFFICE_ADDRESS,
  PHYSICAL_ADDRESS,
  OUT_ADDRESS,
  ACQ_ADDRESS,
  BUSINESS_COMBINATIONS,
  BUSINESS_COMBINATIONS_FAILURES,
  MAIN_OFFICE_RELOCATIONS,
  INTERIM_MERGERS,
  CONVERSIONS,
  TITLE_CHANGES,
  TRUST_POWERS,
  PANDAS,
  BRANCH_OFFICE_RELOCATIONS,
  FROM_ADDRESS,
  OFFICE_ADDRESS,
} from "../../refdata/constants";
import { Divider, Grid, Typography, Box, Theme } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { addressLookup, toTitleCase } from "../../common/services/Utilities";

const useClasses = makeStyles((theme: Theme) => ({
  divider: {
    "&.MuiDivider-root": {
      margin: "15px auto 15px auto",
      borderColor: "#e4eef7",
      padding: "0px",
    },
  },
  gridContainer: {
    "paddingBottom": 16,
    "& .MuiGrid-container": {
      padding: "2px 0px",
      width: "100%",
    },
    "& .MuiGrid-item": {
      "margin": "0 auto",
      "padding": 5,
      "& p": {
        color: "#70643E",
        lineHeight: 1.3,
      },
      "& strong": {
        color: theme.palette.primary.main,
      },
    },
  },
  columnHeader: {
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 5,
  },
  boxStyles: {
    border: "1px solid #a48923",
    borderRadius: "5px",
    backgroundColor: "#f7f6f2",
    margin: "0px 6px 6px 6px",
    height: "calc(100% - 23px)",
  },
}));

const BEFORE_CHANGE = "Before Change";
const AFTER_CHANGE = "After Change";

const OUTGOING_INSTITUTIONS = "Outgoing Institution";
const ACQUIRING_INSTITUTIONS = "Acquiring Institution";
const SURVIVING_INSTITUTIONS = "Surviving Institution";

const eventTypeSecondaryDetails: { [key: string]: string[] } = {
  [BUSINESS_COMBINATIONS]: [
    OUTGOING_INSTITUTIONS,
    ACQUIRING_INSTITUTIONS,
    SURVIVING_INSTITUTIONS,
  ],
  [BUSINESS_COMBINATIONS_FAILURES]: [
    OUTGOING_INSTITUTIONS,
    ACQUIRING_INSTITUTIONS,
    SURVIVING_INSTITUTIONS,
  ],
  [MAIN_OFFICE_RELOCATIONS]: [BEFORE_CHANGE, AFTER_CHANGE],
  [INTERIM_MERGERS]: [BEFORE_CHANGE, AFTER_CHANGE],
  [CONVERSIONS]: [BEFORE_CHANGE, AFTER_CHANGE],
  [TITLE_CHANGES]: [BEFORE_CHANGE, AFTER_CHANGE],
  [TRUST_POWERS]: [BEFORE_CHANGE, AFTER_CHANGE],
  [PANDAS]: [BEFORE_CHANGE, AFTER_CHANGE],
  [BRANCH_OFFICE_RELOCATIONS]: [BEFORE_CHANGE, AFTER_CHANGE],
};

const columnFieldDescriptors: {
  [key: string]: { label: string; fieldValue: string }[];
} = {
  [`${BUSINESS_COMBINATIONS}_${OUTGOING_INSTITUTIONS}`]: [
    {
      label: "Institution Name",
      fieldValue: "OUT_INSTNAME",
    },
    {
      label: "Cert#",
      fieldValue: "OUT_CERT",
    },
    {
      label: "Class",
      fieldValue: "OUT_CLASS",
    },
    {
      label: "County",
      fieldValue: "OUT_CNTYNAME",
    },
    {
      label: "FDIC Region",
      fieldValue: "OUT_FDICREGION_DESC",
    },
    {
      label: "Primary Regulator",
      fieldValue: "OUT_REGAGENT",
    },
    {
      label: "Chartering Authority",
      fieldValue: "OUT_CHARTAGENT",
    },
    {
      label: "Trust",
      fieldValue: "OUT_TRUST",
    },
  ],
  [`${BUSINESS_COMBINATIONS}_${ACQUIRING_INSTITUTIONS}`]: [
    {
      label: "Institution Name",
      fieldValue: "ACQ_INSTNAME",
    },
    {
      label: "Cert#",
      fieldValue: "ACQ_CERT",
    },
    {
      label: "Class",
      fieldValue: "ACQ_CLASS",
    },
    {
      label: "County",
      fieldValue: "ACQ_CNTYNAME",
    },
    {
      label: "FDIC Region",
      fieldValue: "ACQ_FDICREGION_DESC",
    },
    {
      label: "Primary Regulator",
      fieldValue: "ACQ_REGAGENT",
    },
    {
      label: "Chartering Authority",
      fieldValue: "ACQ_CHARTAGENT",
    },
    {
      label: "Trust",
      fieldValue: "ACQ_TRUST",
    },
  ],
  [`${BUSINESS_COMBINATIONS}_${SURVIVING_INSTITUTIONS}`]: [
    {
      label: "Institution Name",
      fieldValue: "SUR_INSTNAME",
    },
    {
      label: "Cert#",
      fieldValue: "SUR_CERT",
    },
    {
      label: "Class",
      fieldValue: "SUR_CLASS",
    },
    {
      label: "County",
      fieldValue: "SUR_CNTYNAME",
    },
    {
      label: "FDIC Region",
      fieldValue: "SUR_FDICREGION_DESC",
    },
    {
      label: "Primary Regulator",
      fieldValue: "SUR_REGAGENT",
    },
    {
      label: "Chartering Authority",
      fieldValue: "SUR_CHARTAGENT",
    },
    {
      label: "Trust",
      fieldValue: "SUR_TRUST",
    },
  ],
  [`${BUSINESS_COMBINATIONS_FAILURES}_${OUTGOING_INSTITUTIONS}`]: [
    {
      label: "Institution Name",
      fieldValue: "OUT_INSTNAME",
    },
    {
      label: "Cert#",
      fieldValue: "OUT_CERT",
    },
    {
      label: "Class",
      fieldValue: "OUT_CLASS",
    },
    {
      label: "County",
      fieldValue: "OUT_CNTYNAME",
    },
    {
      label: "FDIC Region",
      fieldValue: "OUT_FDICREGION_DESC",
    },
    {
      label: "Primary Regulator",
      fieldValue: "OUT_REGAGENT",
    },
    {
      label: "Chartering Authority",
      fieldValue: "OUT_CHARTAGENT",
    },
    {
      label: "Trust",
      fieldValue: "OUT_TRUST",
    },
  ],
  [`${BUSINESS_COMBINATIONS_FAILURES}_${ACQUIRING_INSTITUTIONS}`]: [
    {
      label: "Institution Name",
      fieldValue: "ACQ_INSTNAME",
    },
    {
      label: "Cert#",
      fieldValue: "ACQ_CERT",
    },
    {
      label: "Class",
      fieldValue: "ACQ_CLASS",
    },
    {
      label: "County",
      fieldValue: "ACQ_CNTYNAME",
    },
    {
      label: "FDIC Region",
      fieldValue: "ACQ_FDICREGION_DESC",
    },
    {
      label: "Primary Regulator",
      fieldValue: "ACQ_REGAGENT",
    },
    {
      label: "Chartering Authority",
      fieldValue: "ACQ_CHARTAGENT",
    },
    {
      label: "Trust",
      fieldValue: "ACQ_TRUST",
    },
  ],
  [`${BUSINESS_COMBINATIONS_FAILURES}_${SURVIVING_INSTITUTIONS}`]: [
    {
      label: "Institution Name",
      fieldValue: "SUR_INSTNAME",
    },
    {
      label: "Cert#",
      fieldValue: "SUR_CERT",
    },
    {
      label: "Class",
      fieldValue: "SUR_CLASS",
    },
    {
      label: "County",
      fieldValue: "SUR_CNTYNAME",
    },
    {
      label: "FDIC Region",
      fieldValue: "SUR_FDICREGION_DESC",
    },
    {
      label: "Primary Regulator",
      fieldValue: "SUR_REGAGENT",
    },
    {
      label: "Chartering Authority",
      fieldValue: "SUR_CHARTAGENT",
    },
    {
      label: "Trust",
      fieldValue: "SUR_TRUST",
    },
  ],
  [`${MAIN_OFFICE_RELOCATIONS}_${BEFORE_CHANGE}`]: [
    {
      label: "Address",
      fieldValue: FROM_ADDRESS,
    },
    {
      label: "County",
      fieldValue: "FRM_CNTYNAME",
    },
  ],
  [`${MAIN_OFFICE_RELOCATIONS}_${AFTER_CHANGE}`]: [
    {
      label: "Address",
      fieldValue: PHYSICAL_ADDRESS,
    },
    {
      label: "County",
      fieldValue: "CNTYNAME",
    },
  ],
  [`${BRANCH_OFFICE_RELOCATIONS}_${BEFORE_CHANGE}`]: [
    {
      label: "Address",
      fieldValue: FROM_OFFICE_ADDRESS,
    },
    {
      label: "County",
      fieldValue: "FRM_OFF_CNTYNAME",
    },
  ],
  [`${BRANCH_OFFICE_RELOCATIONS}_${AFTER_CHANGE}`]: [
    {
      label: "Address",
      fieldValue: OFFICE_ADDRESS,
    },
    {
      label: "County",
      fieldValue: "OFF_CNTYNAME",
    },
  ],
  [`${INTERIM_MERGERS}_${BEFORE_CHANGE}`]: [
    {
      label: "Institution Name",
      fieldValue: "FRM_INSTNAME",
    },
    {
      label: "County",
      fieldValue: "FRM_CNTYNAME",
    },
    {
      label: "Chartering Authority",
      fieldValue: "FRM_CHARTAGENT",
    },
  ],
  [`${INTERIM_MERGERS}_${AFTER_CHANGE}`]: [
    {
      label: "Institution Name",
      fieldValue: "INSTNAME",
    },
    {
      label: "County",
      fieldValue: "CNTYNAME",
    },
    {
      label: "Chartering Authority",
      fieldValue: "CHARTAGENT",
    },
  ],
  [`${CONVERSIONS}_${BEFORE_CHANGE}`]: [
    {
      label: "Institution Name",
      fieldValue: "FRM_INSTNAME",
    },
    {
      label: "Class",
      fieldValue: "FRM_CLASS",
    },
    {
      label: "Chartering Authority",
      fieldValue: "FRM_CHARTAGENT",
    },
    {
      label: "Primary Regulator",
      fieldValue: "FRM_REGAGENT",
    },
  ],
  [`${CONVERSIONS}_${AFTER_CHANGE}`]: [
    {
      label: "Institution Name",
      fieldValue: "INSTNAME",
    },
    {
      label: "Class",
      fieldValue: "CLASS",
    },
    {
      label: "Chartering Authority",
      fieldValue: "CHARTAGENT",
    },
    {
      label: "Primary Regulator",
      fieldValue: "REGAGENT",
    },
  ],
  [`${TITLE_CHANGES}_${BEFORE_CHANGE}`]: [
    {
      label: "Institution Name",
      fieldValue: "FRM_INSTNAME",
    },
  ],
  [`${TITLE_CHANGES}_${AFTER_CHANGE}`]: [
    {
      label: "Institution Name",
      fieldValue: "INSTNAME",
    },
  ],
  [`${TRUST_POWERS}_${BEFORE_CHANGE}`]: [
    {
      label: "Trust",
      fieldValue: "FRM_TRUST",
    },
  ],
  [`${TRUST_POWERS}_${AFTER_CHANGE}`]: [
    {
      label: "Trust",
      fieldValue: "TRUST",
    },
  ],
  [`${PANDAS}_${BEFORE_CHANGE}`]: [
    {
      label: "Institution Name",
      fieldValue: "OUT_INSTNAME",
    },
    {
      label: "Main Office Address",
      fieldValue: OUT_ADDRESS,
    },
    {
      label: "Cert#",
      fieldValue: "OUT_CERT",
    },
    {
      label: "Class",
      fieldValue: "OUT_CLASS",
    },
    {
      label: "County",
      fieldValue: "OUT_CNTYNAME",
    },
    {
      label: "FDIC Region",
      fieldValue: "OUT_FDICREGION_DESC",
    },
    {
      label: "Primary Regulator",
      fieldValue: "OUT_REGAGENT",
    },
  ],
  [`${PANDAS}_${AFTER_CHANGE}`]: [
    {
      label: "Institution Name",
      fieldValue: "ACQ_INSTNAME",
    },
    {
      label: "Main Office Address",
      fieldValue: ACQ_ADDRESS,
    },
    {
      label: "Cert#",
      fieldValue: "ACQ_CERT",
    },
    {
      label: "Class",
      fieldValue: "ACQ_CLASS",
    },
    {
      label: "County",
      fieldValue: "ACQ_CNTYNAME",
    },
    {
      label: "FDIC Region",
      fieldValue: "ACQ_FDICREGION_DESC",
    },
    {
      label: "Primary Regulator",
      fieldValue: "ACQ_REGAGENT",
    },
  ],
};
const titleCaseLookup = [
  "FRM_CNTYNAME",
  "CNTYNAME",
  "ACQ_CNTYNAME",
  "OUT_CNTYNAME",
  "SUR_CNTYNAME",
  "SUR_FDICREGION_DESC",
  "OUT_FDICREGION_DESC",
  "ACQ_FDICREGION_DESC",
  "FRM_OFF_CNTYNAME",
  "OFF_CNTYNAME",
];
const Column = (eventType: string, data: OscrLevelTwoRecord) => {
  return (
    <>
      {columnFieldDescriptors[eventType].map(
        (obj: KeyStringValue, index: number) => {
          const formatFieldValue = titleCaseLookup.includes(obj.fieldValue)
            ? toTitleCase(data[obj.fieldValue] as string)
            : data[obj.fieldValue];
          return (
            <Grid container key={`${eventType}-${index}`}>
              <Grid item xs={6}>
                <Typography
                  id={`${obj.fieldValue}_TITLE`}
                  color="primary"
                  align="right"
                >
                  <strong>{obj.label}</strong>
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  id={`${obj.fieldValue}_VALUE`}
                  align="left"
                  component={
                    obj.label.toLowerCase().includes("address") ? "div" : "p"
                  }
                >
                  {obj.label.toLowerCase().includes("address") ? (
                    <AddressDisplay
                      addressObject={addressLookup(data)[obj.fieldValue]}
                      wrapAddress={true}
                      align={"left"}
                    />
                  ) : (
                    formatFieldValue
                  )}
                </Typography>
              </Grid>
            </Grid>
          );
        },
      )}
    </>
  );
};

export default function OSCRSecondaryDetails({
  eventType,
  data,
}: {
  eventType: string;
  data: OscrLevelTwoRecord;
}) {
  const classes = useClasses();
  if (!eventTypeSecondaryDetails[eventType]) {
    return <></>;
  }
  return (
    <>
      <Divider className={classes.divider} />
      <Grid container className={classes.gridContainer} alignContent={"center"}>
        {eventTypeSecondaryDetails[eventType].map((type: string) => {
          return (
            <Grid item xs key={`${type}`}>
              <Typography
                id={`${type.replace(/\s+/g, "-")}`}
                className={classes.columnHeader}
              >
                {eventType === PANDAS &&
                type === BEFORE_CHANGE &&
                !data.OUT_CERT
                  ? `${type}*`
                  : type}
              </Typography>
              <Box className={classes.boxStyles}>
                {Column(`${eventType}_${type}`, data)}
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </>
  );
}
